"use client";
/**
 * What the shell tells the Dock window (contract 2026-09-23): the tasks the
 * notch's dots showed, a moment the notch would have drawn, and the Mac's
 * runner and session. Each event carries the whole state, so the store holds
 * the last one heard and nothing else. Only the window host subscribes; on
 * the web and in the shell's main window the store stays empty, so nothing
 * it feeds is drawn.
 */
import { useEffect, useSyncExternalStore } from "react";
import { create } from "zustand";
import {
  isDockWindow,
  subscribeDockShell,
  type DockMoment,
  type DockStatus,
  type DockTask,
} from "@/lib/dock-window";
import type { FleetRow } from "@/components/composer/fleet";

interface DockShellState {
  tasks: DockTask[];
  moment: DockMoment | null;
  status: DockStatus | null;
  /** Done tasks whose answer the person already opened or put away. */
  seenAnswers: string[];
  /** The reply to an accepted offer (the notch showed it), until put away. */
  reply: DockReply | null;
}

export interface DockReply {
  title: string;
  text: string;
  /** The task the turn started or followed, when it did: the reply opens it. */
  taskId?: string;
}

/** Put-away answers survive a panel reload (the page's session), never a relaunch. */
const SEEN_KEY = "vestra.dock.seenAnswers";
function readSeen(): string[] {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? "[]");
    return Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

export const useDockShellStore = create<DockShellState>(() => ({
  tasks: [],
  moment: null,
  status: null,
  seenAnswers: typeof window === "undefined" ? [] : readSeen(),
  reply: null,
}));

/** The answer to bloom: the newest done task with one the person has not put away. */
export function bloomOf(
  tasks: DockTask[],
  seen: readonly string[]
): DockTask | null {
  return (
    tasks.find((t) => t.state === "done" && t.answer && !seen.includes(t.id)) ??
    null
  );
}

/** The bloom is put away (opened or dismissed) and never comes back for that task. */
export function putAnswerAway(taskId: string): void {
  const s = useDockShellStore.getState();
  if (s.seenAnswers.includes(taskId)) return;
  // The shell keeps at most 8 tasks, so the ledger never needs more than that.
  const seenAnswers = [...s.seenAnswers, taskId].slice(-32);
  useDockShellStore.setState({ seenAnswers });
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(seenAnswers));
  } catch {
    /* the bloom simply may return after a reload */
  }
}

const noSubscribe = () => () => {};

/** The shell's panel, read hydration-safe (false on the server and through hydration). */
export function useInDockWindow(): boolean {
  return useSyncExternalStore(
    noSubscribe,
    () => isDockWindow(),
    () => false
  );
}

/** The window host listens (then tells the shell it is ready); every other host subscribes to nothing. */
export function useDockShell(on: boolean): void {
  useEffect(() => {
    if (!on) return;
    return subscribeDockShell({
      onTasks: (tasks) => useDockShellStore.setState({ tasks }),
      onMoment: (moment) => useDockShellStore.setState({ moment }),
      onStatus: (status) => useDockShellStore.setState({ status }),
    });
  }, [on]);
}

/** The ball's one line about the Mac (the notch's Offline and SignedOut wings); null while all is well. */
export function dockStatusLine(status: DockStatus | null): string | null {
  if (!status) return null;
  if (!status.signedIn) return "Sign in to Vestra";
  if (status.runner === "offline") return "Your Mac is offline";
  if (status.runner === "starting") return "Your Mac is starting up";
  return null;
}

const SUB: Record<DockTask["state"], string> = {
  working: "Working on it",
  needs: "Waiting for you",
  done: "Done",
  failed: "Stopped with a problem",
};

/**
 * The shell's tasks as the island's rows: the Dock's shelf is the island it
 * already has (one face), so the notch's shelf reads in the island's own
 * words. A failed task asks for a look, so it wears the needs-you chip.
 */
export function dockTaskRows(tasks: DockTask[]): FleetRow[] {
  return tasks.map((t) => ({
    id: t.id,
    state: t.state === "failed" ? "needs" : t.state,
    title: t.title,
    sub: t.answer ?? SUB[t.state],
    ago: "",
    interaction: null,
  }));
}
